import React from 'react';
import { ComboboxItem } from '@mantine/core';
import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { MultiSelect } from 'src/design/atoms/MultiSelect';
import { JobUserDto } from 'src/api/FiretrolApiClient';
import { useJobs } from 'src/hooks/useJobs';
import { toMultiSelectValue } from 'src/design/atoms/toMultiSelectValue';

export interface IJobUserSelectProps {
  onChange: (value: JobUserDto[] | null) => void;
  value: JobUserDto[] | null;
  hideLabel?: boolean;
  placeholder?: string;
  hidden?: boolean;
  disabled?: boolean;
}

export const JobUserSelect: React.FC<IJobUserSelectProps> = ({
  onChange,
  disabled = false,
  hidden = false,
  value,
  hideLabel = false,
  placeholder = 'Select users...',
}) => {
  const { team } = useJobs();

  const userData = React.useMemo(() => {
    return team.map((user) => ({
      value: String(user.user?.id),
      label: `${user.user?.firstName} ${user.user?.lastName}`,
    }));
  }, [team]);

  const renderOption = ({ option }: { option: ComboboxItem }) => {
    const user = team.find((u) => String(u.user?.id) === option.value);
    if (!user || !user.user) return option.label;

    return (
      <Group justify="space-between" wrap="nowrap">
        <span>
          {user.user.firstName} {user.user.lastName}
        </span>
        <Group gap="xs" wrap="nowrap">
          {user.user.userOrganizationRole?.map((r) => {
            return (
              <Badge color="blue" variant="filled" key={r.id}>
                {r.organizationRole?.name}
              </Badge>
            );
          })}
        </Group>
      </Group>
    );
  };

  const handleChange = (selectedValues: string[]) => {
    if (selectedValues.length > 0) {
      const selectedUsers = selectedValues
        .map((id) => team.find((u) => String(u.user?.id) === id))
        .filter((u): u is JobUserDto => u !== undefined);
      onChange(selectedUsers);
    } else {
      onChange([]);
    }
  };

  if (hidden)
    return <div style={{ marginBottom: 'var(--mantine-spacing-xl)' }}></div>;
  return (
    <MultiSelect
      label={!hideLabel ? 'User' : undefined}
      placeholder={placeholder}
      data={userData}
      value={toMultiSelectValue(
        (value ?? []).map((v) => String(v.user?.id)),
        userData,
      )}
      onChange={handleChange}
      disabled={disabled}
      clearable
      searchable
      renderOption={renderOption}
      mb="xl"
    />
  );
};
